import {useState} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useVehicle from "../hooks/useVehicle";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import VehicleTypesDropdownComponent from "../common/vehicle-type-dropdown.component";


const CreateVehicleComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {isAddingVehicle, createVehicle} = useVehicle();

    const [selectedVehicleType, setSelectedVehicleType] = useState(null);
    const [plateNoInput, setPlateNoInput] = useState(null);
    const [modelInput, setModelInput] = useState(null);
    const [orcrInput, setOrcrInput] = useState(null);

    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    const handleVehicleTypeChange = (value) => {
        setSelectedVehicleType(value);
    }

    const handleTextfieldChange = (e, type) => {
        const {value} = e.target;

        switch (type) {
            case "plateNo":
                setPlateNoInput(value);
                break;

            case "model":
                setModelInput(value);
                break;

            case "orcr":
                setOrcrInput(value);
                break;

            default:
                break;
        }
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/vehicles");
        }
    }

    const handleCreateVehicle = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            let driver_uid = "";
            let container_uid = "";

            const payload = {
                type: selectedVehicleType,
                plate_number: plateNoInput,
                model_manufacturer: modelInput,
                or_cr: orcrInput,
                driver_uid: driver_uid,
                container_uid: container_uid,
                org_uid: organizationId,
            };

            createVehicle(payload, orgIdFromUrlForPortEmployee);
            
        } else if (!organizationId) {
            let driver_uid = "";
            let container_uid = "";
            let org_uid = "";

            const payload = {
                type: selectedVehicleType,
                plate_number: plateNoInput,
                model_manufacturer: modelInput,
                or_cr: orcrInput,
                driver_uid: driver_uid,
                container_uid: container_uid,
                org_uid: org_uid,
            };

            createVehicle(payload);
        }
    }
    

    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Vehicle List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "900px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Create New Vehicle"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Add new vehicle for entry"}  
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Vehicle Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to create a new vehicle"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Vehicle Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <VehicleTypesDropdownComponent 
                                    value={selectedVehicleType}
                                    onChange={handleVehicleTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isAddingVehicle}
                                />
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Model & Manufacturer"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter model & manufacturer"}
                                    variant={"outlined"}
                                    value={modelInput}
                                    allowedPattern={/[^a-zA-Z0-9\s]/g}
                                    onChange={(e) => handleTextfieldChange(e, "model")}
                                    fullWidth={true}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isAddingVehicle}
                                />  
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Plate Number"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter plate number"}
                                    variant={"outlined"}
                                    value={plateNoInput}
                                    onChange={(e) => handleTextfieldChange(e, "plateNo")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9-]/g}
                                    hiddenLabel
                                    readOnly={isAddingVehicle}
                                />           
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"OR/CR"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter OR/CR"}
                                    variant={"outlined"}
                                    value={orcrInput}
                                    onChange={(e) => handleTextfieldChange(e, "orcr")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9-]/g}
                                    hiddenLabel
                                    readOnly={isAddingVehicle}
                                />  
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isAddingVehicle) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Add Vehicle"}
                            startIcon={isAddingVehicle && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (plateNoInput && selectedVehicleType && modelInput && orcrInput && !isAddingVehicle) ? "100%" : "55%",
                                cursor: (plateNoInput && selectedVehicleType && modelInput && orcrInput && !isAddingVehicle) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={
                                (plateNoInput && selectedVehicleType && modelInput && orcrInput && !isAddingVehicle) ? handleCreateVehicle : null
                            }
                        />    
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default CreateVehicleComponent